// src/components/AdminPanel/AdminUserGatherings.js
import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { adminApi } from '../../services/adminApi';

/**
 * GatheringRow - Individual row component with hover state
 */
function GatheringRow({
  g,
  idx,
  isEditing,
  editValues,
  venues,
  loading,
  formatDateTime,
  needsSignupAlert,
  isRatioImbalanced,
  getMaleToFemaleRatio,
  calculateDaysUntilSince,
  handleEditChange,
  saveGatheringUpdates,
  cancelEditing,
  startEditing,
  handleDetailsClick
}) {
  const [isHovered, setIsHovered] = useState(false);
  const isUnavailable = g.available === false;

  return (
    <tr
      style={{
        borderBottom: '1px solid #e9ecef',
        backgroundColor: isUnavailable ? '#f3f4f6' : (isHovered ? '#f8f9fa' : (idx % 2 === 0 ? 'white' : '#fafbfc')),
        opacity: isUnavailable ? 0.85 : 1,
        transition: 'background-color 0.15s ease'
      }}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      <td style={{ padding: '0.75rem', fontSize: '0.9rem', color: '#212529' }}>{formatDateTime(g.gathering_datetime)}</td>
      <td style={{ padding: '0.75rem', fontSize: '0.9rem', color: '#212529' }}>{g.city || 'N/A'}</td>
      <td style={{ padding: '0.75rem', fontSize: '0.9rem', color: '#212529' }}>{g.area || 'N/A'}</td>
      <td style={{ padding: '0.75rem', fontSize: '0.9rem', color: '#212529' }}>{g.theme_name || 'No Theme'}</td>
      <td style={{ padding: '0.75rem', textAlign: 'center' }}>
        <span style={{
          display: 'inline-block',
          padding: '0.25rem 0.5rem',
          border: needsSignupAlert(g.signup_count, g.gathering_datetime) ? '3px solid #dc3545' : 'none',
          backgroundColor: needsSignupAlert(g.signup_count, g.gathering_datetime) ? '#f8d7da' : 'transparent',
          borderRadius: '4px',
          fontWeight: needsSignupAlert(g.signup_count, g.gathering_datetime) ? '600' : 'normal',
          fontSize: '0.9rem',
          color: needsSignupAlert(g.signup_count, g.gathering_datetime) ? '#721c24' : '#212529'
        }}>
          {g.signup_count}
        </span>
      </td>
      <td style={{ padding: '0.75rem', textAlign: 'center' }}>
        <span style={{
          display: 'inline-block',
          padding: '0.25rem 0.5rem',
          border: isRatioImbalanced(g.male_count, g.female_count) ? '3px solid #ffc107' : 'none',
          backgroundColor: isRatioImbalanced(g.male_count, g.female_count) ? '#fff3cd' : 'transparent',
          borderRadius: '4px',
          fontWeight: isRatioImbalanced(g.male_count, g.female_count) ? '600' : 'normal',
          fontSize: '0.9rem',
          color: isRatioImbalanced(g.male_count, g.female_count) ? '#856404' : '#212529'
        }}>
          {getMaleToFemaleRatio(g.male_count, g.female_count)}
        </span>
      </td>
      <td style={{ padding: '0.75rem', textAlign: 'center', fontSize: '0.9rem', color: '#212529' }}>
        {calculateDaysUntilSince(g.gathering_datetime)}
      </td>
      <td style={{ padding: '0.75rem', textAlign: 'center', fontSize: '0.9rem' }}>
        <span style={{
          color: isUnavailable ? '#dc2626' : '#16a34a',
          fontWeight: '700'
        }}>
          {isUnavailable ? 'No' : 'Yes'}
        </span>
      </td>
      <td style={{ padding: '0.75rem', fontSize: '0.9rem', color: '#212529' }}>
        {isEditing ? (
          <select
            value={editValues.customer_manager}
            onChange={(e) => handleEditChange('customer_manager', e.target.value)}
            style={{
              width: '100%',
              padding: '0.4rem',
              border: '1px solid #ced4da',
              borderRadius: '4px',
              fontSize: '0.9rem'
            }}
          >
            <option value="">--</option>
            <option value="Andy">Andy</option>
            <option value="Matt">Matt</option>
          </select>
        ) : (
          g.customer_manager || '--'
        )}
      </td>
      <td style={{ padding: '0.75rem', fontSize: '0.9rem', color: '#212529' }}>
        {isEditing ? (
          <select
            value={editValues.venue_id}
            onChange={(e) => handleEditChange('venue_id', e.target.value)}
            style={{
              width: '100%',
              padding: '0.4rem',
              border: '1px solid #ced4da',
              borderRadius: '4px',
              fontSize: '0.9rem'
            }}
          >
            <option value="">--</option>
            {venues.map((venue) => (
              <option key={venue.id} value={venue.id}>
                {venue.name}
              </option>
            ))}
          </select>
        ) : (
          g.venue_name || '--'
        )}
      </td>
      <td style={{ padding: '0.75rem', fontSize: '0.9rem', color: '#212529', textAlign: 'center' }}>
        {isEditing ? (
          <select
            value={editValues.venue_booked}
            onChange={(e) => handleEditChange('venue_booked', e.target.value)}
            style={{
              width: '100%',
              padding: '0.4rem',
              border: '1px solid #ced4da',
              borderRadius: '4px',
              fontSize: '0.9rem'
            }}
          >
            <option value="">--</option>
            <option value="yes">yes</option>
          </select>
        ) : (
          <span style={{
            display: 'inline-block',
            padding: '0.2rem 0.6rem',
            borderRadius: '12px',
            fontSize: '0.8rem',
            fontWeight: '500',
            backgroundColor: g.venue_booked === 'yes' ? '#d4edda' : '#f8f9fa',
            color: g.venue_booked === 'yes' ? '#155724' : '#6c757d'
          }}>
            {g.venue_booked || '--'}
          </span>
        )}
      </td>
      <td style={{ padding: '0.75rem', fontSize: '0.9rem', color: '#212529', textAlign: 'center' }}>
        {isEditing ? (
          <select
            value={editValues.venue_phoned}
            onChange={(e) => handleEditChange('venue_phoned', e.target.value)}
            style={{
              width: '100%',
              padding: '0.4rem',
              border: '1px solid #ced4da',
              borderRadius: '4px',
              fontSize: '0.9rem'
            }}
          >
            <option value="">--</option>
            <option value="yes">yes</option>
          </select>
        ) : (
          <span style={{
            display: 'inline-block',
            padding: '0.2rem 0.6rem',
            borderRadius: '12px',
            fontSize: '0.8rem',
            fontWeight: '500',
            backgroundColor: g.venue_phoned === 'yes' ? '#d4edda' : '#f8f9fa',
            color: g.venue_phoned === 'yes' ? '#155724' : '#6c757d'
          }}>
            {g.venue_phoned || '--'}
          </span>
        )}
      </td>
      <td style={{ padding: '0.75rem' }}>
        {isEditing ? (
          <div style={{ display: 'flex', gap: '0.4rem' }}>
            <button
              onClick={() => saveGatheringUpdates(g.gathering_id)}
              disabled={loading}
              style={{
                padding: '0.4rem 0.8rem',
                cursor: loading ? 'not-allowed' : 'pointer',
                backgroundColor: loading ? '#9fcdae' : '#28a745',
                color: 'white',
                border: 'none',
                borderRadius: '4px',
                fontSize: '0.85rem',
                fontWeight: '500',
                transition: 'background-color 0.2s'
              }}
              onMouseOver={(e) => !loading && (e.target.style.backgroundColor = '#218838')}
              onMouseOut={(e) => !loading && (e.target.style.backgroundColor = '#28a745')}
            >
              Save
            </button>
            <button
              onClick={cancelEditing}
              disabled={loading}
              style={{
                padding: '0.4rem 0.8rem',
                cursor: loading ? 'not-allowed' : 'pointer',
                backgroundColor: loading ? '#9ca3a8' : '#6c757d',
                color: 'white',
                border: 'none',
                borderRadius: '4px',
                fontSize: '0.85rem',
                fontWeight: '500',
                transition: 'background-color 0.2s'
              }}
              onMouseOver={(e) => !loading && (e.target.style.backgroundColor = '#5a6268')}
              onMouseOut={(e) => !loading && (e.target.style.backgroundColor = '#6c757d')}
            >
              Cancel
            </button>
          </div>
        ) : (
          <div style={{ display: 'flex', gap: '0.4rem' }}>
            <button
              onClick={() => startEditing(g)}
              disabled={!g.gathering_id}
              style={{
                padding: '0.4rem 0.8rem',
                cursor: g.gathering_id ? 'pointer' : 'not-allowed',
                backgroundColor: g.gathering_id ? '#ffc107' : '#e0e0e0',
                color: g.gathering_id ? '#212529' : '#6c757d',
                border: 'none',
                borderRadius: '4px',
                fontSize: '0.85rem',
                fontWeight: '500',
                transition: 'background-color 0.2s'
              }}
              onMouseOver={(e) => g.gathering_id && (e.target.style.backgroundColor = '#e0a800')}
              onMouseOut={(e) => g.gathering_id && (e.target.style.backgroundColor = '#ffc107')}
            >
              Edit
            </button>
            <button
              onClick={() => handleDetailsClick(g)}
              style={{
                padding: '0.4rem 0.8rem',
                cursor: 'pointer',
                backgroundColor: '#007bff',
                color: 'white',
                border: 'none',
                borderRadius: '4px',
                fontSize: '0.85rem',
                fontWeight: '500',
                transition: 'background-color 0.2s'
              }}
              onMouseOver={(e) => e.target.style.backgroundColor = '#0056b3'}
              onMouseOut={(e) => e.target.style.backgroundColor = '#007bff'}
            >
              Details
            </button>
          </div>
        )}
      </td>
    </tr>
  );
}

/**
 * AdminUserGatherings
 *
 * Displays unique gatherings grouped by (Date, City, Area, Theme) with:
 * - Signup count
 * - Male to Female ratio
 * - Sortable columns
 * - Date range filter
 * - Details button to navigate to User-Slots with filters
 */
function AdminUserGatherings({ adminToken }) {
  const [searchParams, setSearchParams] = useSearchParams();
  const [gatherings, setGatherings] = useState([]);
  const [venues, setVenues] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [earliestDate, setEarliestDate] = useState('');
  const [latestDate, setLatestDate] = useState('');
  const [includePast, setIncludePast] = useState(false);
  const [sortConfig, setSortConfig] = useState({
    key: null,
    direction: 'asc'
  });
  const [editingGatheringId, setEditingGatheringId] = useState(null);
  const [editValues, setEditValues] = useState({});
  const [showUnavailable, setShowUnavailable] = useState(false); // Toggle for showing unavailable gatherings

  // Fetch gatherings summary
  async function fetchGatheringsSummary() {
    if (!adminToken) return;
    try {
      setLoading(true);
      const filters = {};
      if (earliestDate) filters.earliest_date = new Date(earliestDate).toISOString();
      if (latestDate) {
        // Set to end of day for latest date
        const endOfDay = new Date(latestDate);
        endOfDay.setHours(23, 59, 59, 999);
        filters.latest_date = endOfDay.toISOString();
      }

      const data = await adminApi.getGatheringsSummary(filters, adminToken);
      setGatherings(data);
      setError('');
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }

  // Fetch venues
  async function fetchVenues() {
    if (!adminToken) return;
    try {
      const data = await adminApi.getVenuesWithLocation(adminToken, {});
      setVenues(data);
    } catch (err) {
      console.error('Error fetching venues:', err);
    }
  }

  // Fetch on mount or when adminToken changes
  useEffect(() => {
    fetchGatheringsSummary();
    fetchVenues();
    // eslint-disable-next-line
  }, [adminToken]);

  // Handle filter submit
  function handleFilterSubmit(e) {
    e.preventDefault();
    fetchGatheringsSummary();
  }

  // Calculate male to female ratio
  function getMaleToFemaleRatio(maleCount, femaleCount) {
    // If values are null/undefined, return N/A
    if (maleCount == null && femaleCount == null) return 'N/A';

    // Convert to numbers, defaulting to 0
    const male = Number(maleCount) || 0;
    const female = Number(femaleCount) || 0;

    // Show actual counts instead of simplified ratio
    return `${male}:${female}`;
  }

  // Check if ratio is more than double (needs yellow border)
  function isRatioImbalanced(maleCount, femaleCount) {
    if (femaleCount === 0 || maleCount === 0) return false;
    const ratio = maleCount / femaleCount;
    return ratio > 2 || ratio < 0.5;
  }

  // Check if signup count needs red border (< 10 and < 7 days)
  function needsSignupAlert(signupCount, gatheringDateTime) {
    if (signupCount >= 10) return false;
    const daysUntil = calculateDaysUntilSince(gatheringDateTime);
    if (daysUntil === 'N/A') return false;
    const numDays = parseInt(daysUntil);
    return !isNaN(numDays) && numDays >= 0 && numDays < 7;
  }

  // Format datetime for display
  function formatDateTime(isoString) {
    if (!isoString) return '';
    const date = new Date(isoString);

    // Get day of week (e.g., "Fri")
    const dayOfWeek = date.toLocaleString('en-US', { weekday: 'short' });

    // Get month (e.g., "Jan")
    const month = date.toLocaleString('en-US', { month: 'short' });

    // Get day (e.g., "9")
    const day = date.getDate();

    // Get hour in 12-hour format
    let hour = date.getHours();
    const ampm = hour >= 12 ? 'pm' : 'am';
    hour = hour % 12;
    hour = hour ? hour : 12; // Convert 0 to 12

    return `${dayOfWeek} ${month} ${day} at ${hour}${ampm}`;
  }

  // Handle Details button click - navigate to User-Slots with filters
  function handleDetailsClick(gathering) {
    const date = gathering.gathering_datetime ? new Date(gathering.gathering_datetime).toISOString().split('T')[0] : '';
    setSearchParams({
      tab: 'userSlots',
      date: date,
      city: gathering.city || '',
      area: gathering.area || '',
      theme: gathering.theme_name || ''
    });
  }

  // Sorting function
  function handleSort(key) {
    let direction = 'asc';
    if (sortConfig.key === key && sortConfig.direction === 'asc') {
      direction = 'desc';
    }
    setSortConfig({ key, direction });
  }

  // Apply sorting to gatherings
  function getSortedGatherings() {
    // Filter out past gatherings if includePast is false
    let filteredGatherings = gatherings;
    if (!includePast) {
      const now = new Date();
      filteredGatherings = gatherings.filter(g => {
        if (!g.gathering_datetime) return true;
        return new Date(g.gathering_datetime) >= now;
      });
    }

    // Filter out unavailable gatherings if showUnavailable is false
    if (!showUnavailable) {
      filteredGatherings = filteredGatherings.filter(g => g.available !== false);
    }

    if (!sortConfig.key) {
      // Default sort: Date (ascending - earliest first) > City > Area > Theme
      return [...filteredGatherings].sort((a, b) => {
        // Sort by date first (ascending - earliest first)
        const dateA = new Date(a.gathering_datetime);
        const dateB = new Date(b.gathering_datetime);
        if (dateA < dateB) return -1;
        if (dateA > dateB) return 1;

        // Then by city
        const cityCompare = (a.city || '').localeCompare(b.city || '');
        if (cityCompare !== 0) return cityCompare;

        // Then by area
        const areaCompare = (a.area || '').localeCompare(b.area || '');
        if (areaCompare !== 0) return areaCompare;

        // Finally by theme
        return (a.theme_name || '').localeCompare(b.theme_name || '');
      });
    }

    const sortedGatherings = [...filteredGatherings].sort((a, b) => {
      let aVal = a[sortConfig.key];
      let bVal = b[sortConfig.key];

      // Handle datetime sorting
      if (sortConfig.key === 'gathering_datetime') {
        aVal = new Date(aVal);
        bVal = new Date(bVal);
      }

      // Handle numeric sorting
      if (sortConfig.key === 'signup_count') {
        aVal = Number(aVal) || 0;
        bVal = Number(bVal) || 0;
      }

      // Handle string sorting
      if (typeof aVal === 'string') {
        aVal = aVal || '';
        bVal = bVal || '';
        const comparison = aVal.localeCompare(bVal);
        return sortConfig.direction === 'asc' ? comparison : -comparison;
      }

      // Handle numeric/date sorting
      if (aVal < bVal) return sortConfig.direction === 'asc' ? -1 : 1;
      if (aVal > bVal) return sortConfig.direction === 'asc' ? 1 : -1;
      return 0;
    });

    return sortedGatherings;
  }

  const sortedGatherings = getSortedGatherings();

  // Calculate total signups across all visible gatherings
  const totalSignups = sortedGatherings.reduce((sum, gathering) => {
    return sum + (gathering.signup_count || 0);
  }, 0);

  // Calculate total gender counts across all visible gatherings
  const totalMale = sortedGatherings.reduce((sum, gathering) => {
    return sum + (gathering.male_count || 0);
  }, 0);

  const totalFemale = sortedGatherings.reduce((sum, gathering) => {
    return sum + (gathering.female_count || 0);
  }, 0);

  const totalOther = sortedGatherings.reduce((sum, gathering) => {
    return sum + (gathering.other_count || 0);
  }, 0);

  // Calculate Female %
  const femalePercentage = totalSignups > 0
    ? Math.round((totalFemale / totalSignups) * 100)
    : 0;

  // Calculate overall gender ratio (Male:Female)
  const overallGenderRatio = totalFemale > 0
    ? `${totalMale}:${totalFemale}`
    : 'N/A';

  // Calculate unavailable count from all gatherings (before filtering)
  const unavailableCount = gatherings.filter(g => {
    // Also apply past filter if needed
    if (!includePast) {
      const now = new Date();
      if (g.gathering_datetime && new Date(g.gathering_datetime) < now) {
        return false;
      }
    }
    return g.available === false;
  }).length;

  // Helper to render sort indicator
  function getSortIndicator(columnKey) {
    if (sortConfig.key !== columnKey) return null;
    return sortConfig.direction === 'asc' ? ' ▲' : ' ▼';
  }

  // Calculate days until/since gathering
  function calculateDaysUntilSince(gatheringDateTime) {
    if (!gatheringDateTime) return 'N/A';
    const now = new Date();
    const gatheringDate = new Date(gatheringDateTime);
    const diffTime = gatheringDate - now;
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

    if (diffDays < 0) {
      return `(${Math.abs(diffDays)})`;
    }
    return diffDays.toString();
  }

  // Start editing a gathering
  function startEditing(gathering) {
    setEditingGatheringId(gathering.gathering_id);
    setEditValues({
      customer_manager: gathering.customer_manager || '',
      venue_id: gathering.venue_id || '',
      venue_booked: gathering.venue_booked || '',
      venue_phoned: gathering.venue_phoned || ''
    });
  }

  // Cancel editing
  function cancelEditing() {
    setEditingGatheringId(null);
    setEditValues({});
  }

  // Save gathering updates
  async function saveGatheringUpdates(gatheringId) {
    if (!adminToken || !gatheringId) return;
    try {
      setLoading(true);
      await adminApi.updateGatheringDetails(gatheringId, editValues, adminToken);
      await fetchGatheringsSummary();
      cancelEditing();
      setError('');
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }

  // Handle edit value changes
  function handleEditChange(field, value) {
    setEditValues(prev => ({
      ...prev,
      [field]: value
    }));
  }

  return (
    <div className="admin-gatherings-container" style={{ padding: '1rem' }}>
      <style>{`
        @media (max-width: 768px) {
          .admin-gatherings-container {
            padding: 2px !important;
          }
          .admin-gatherings-form {
            padding: 0.25rem !important;
            gap: 0.25rem !important;
          }
          .admin-gatherings-form input,
          .admin-gatherings-form button {
            padding: 0.3rem 0.5rem !important;
            font-size: 0.75rem !important;
          }
          .admin-gatherings-form label {
            font-size: 0.7rem !important;
          }
          .admin-gatherings-table {
            min-width: 100% !important;
            font-size: 0.65rem !important;
          }
          .admin-gatherings-table th,
          .admin-gatherings-table td {
            padding: 0.2rem !important;
            font-size: 0.65rem !important;
            word-wrap: break-word;
            white-space: normal;
            line-height: 1.2;
            vertical-align: top;
          }
          .admin-gatherings-table th:nth-child(1),
          .admin-gatherings-table td:nth-child(1) {
            max-width: 70px !important;
          }
          .admin-gatherings-table th:nth-child(2),
          .admin-gatherings-table td:nth-child(2) {
            max-width: 55px !important;
          }
          .admin-gatherings-table th:nth-child(3),
          .admin-gatherings-table td:nth-child(3) {
            max-width: 55px !important;
          }
          .admin-gatherings-table th:nth-child(4),
          .admin-gatherings-table td:nth-child(4) {
            max-width: 65px !important;
          }
          .admin-gatherings-table th:nth-child(5),
          .admin-gatherings-table td:nth-child(5) {
            max-width: 40px !important;
          }
          .admin-gatherings-table th:nth-child(6),
          .admin-gatherings-table td:nth-child(6) {
            max-width: 45px !important;
          }
          .admin-gatherings-table th:nth-child(7),
          .admin-gatherings-table td:nth-child(7) {
            max-width: 45px !important;
          }
          .admin-gatherings-table th:nth-child(8),
          .admin-gatherings-table td:nth-child(8) {
            max-width: 40px !important;
          }
          .admin-gatherings-table th:nth-child(9),
          .admin-gatherings-table td:nth-child(9) {
            max-width: 50px !important;
          }
          .admin-gatherings-table th:nth-child(10),
          .admin-gatherings-table td:nth-child(10) {
            max-width: 55px !important;
          }
          .admin-gatherings-table th:nth-child(11),
          .admin-gatherings-table td:nth-child(11) {
            max-width: 45px !important;
          }
          .admin-gatherings-table th:nth-child(12),
          .admin-gatherings-table td:nth-child(12) {
            max-width: 45px !important;
          }
          .admin-gatherings-table th:nth-child(13),
          .admin-gatherings-table td:nth-child(13) {
            max-width: 65px !important;
          }
          .admin-gatherings-header h2 {
            font-size: 1.2rem !important;
            margin-bottom: 0.2rem !important;
          }
          .admin-gatherings-header p {
            font-size: 0.75rem !important;
            margin-bottom: 0.5rem !important;
          }
          .admin-total-count {
            padding: 0.3rem !important;
            margin-bottom: 0.3rem !important;
            font-size: 0.75rem !important;
          }
        }
      `}</style>
      <div className="admin-gatherings-header">
        <h2 style={{ marginBottom: '0.5rem', fontSize: '1.8rem', fontWeight: '600' }}>Gatherings</h2>
        <p style={{ marginBottom: '1.5rem', color: '#666', fontSize: '0.95rem' }}>
          Showing unique gatherings grouped by Date, City, Area, and Theme.
        </p>
      </div>

      {/* Date Range Filter */}
      <form
        className="admin-gatherings-form"
        onSubmit={handleFilterSubmit}
        style={{
          display: 'flex',
          gap: '1rem',
          marginBottom: '1.5rem',
          alignItems: 'center',
          padding: '1rem',
          backgroundColor: '#f8f9fa',
          borderRadius: '8px',
          border: '1px solid #e0e0e0'
        }}
      >
        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.25rem' }}>
          <label style={{ fontSize: '0.85rem', fontWeight: '500', color: '#555' }}>Earliest Date:</label>
          <input
            type="date"
            value={earliestDate}
            onChange={(e) => setEarliestDate(e.target.value)}
            style={{
              padding: '0.5rem',
              border: '1px solid #d0d0d0',
              borderRadius: '4px',
              fontSize: '0.9rem'
            }}
          />
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.25rem' }}>
          <label style={{ fontSize: '0.85rem', fontWeight: '500', color: '#555' }}>Latest Date:</label>
          <input
            type="date"
            value={latestDate}
            onChange={(e) => setLatestDate(e.target.value)}
            style={{
              padding: '0.5rem',
              border: '1px solid #d0d0d0',
              borderRadius: '4px',
              fontSize: '0.9rem'
            }}
          />
        </div>
        <button
          type="submit"
          style={{
            padding: '0.6rem 1.2rem',
            backgroundColor: '#007bff',
            color: 'white',
            border: 'none',
            borderRadius: '4px',
            cursor: 'pointer',
            fontSize: '0.9rem',
            fontWeight: '500',
            marginTop: '1.4rem',
            transition: 'background-color 0.2s'
          }}
          onMouseOver={(e) => e.target.style.backgroundColor = '#0056b3'}
          onMouseOut={(e) => e.target.style.backgroundColor = '#007bff'}
        >
          Apply Filter
        </button>
        <button
          type="button"
          onClick={() => {
            setEarliestDate('');
            setLatestDate('');
            setTimeout(() => fetchGatheringsSummary(), 0);
          }}
          style={{
            padding: '0.6rem 1.2rem',
            backgroundColor: '#6c757d',
            color: 'white',
            border: 'none',
            borderRadius: '4px',
            cursor: 'pointer',
            fontSize: '0.9rem',
            fontWeight: '500',
            marginTop: '1.4rem',
            transition: 'background-color 0.2s'
          }}
          onMouseOver={(e) => e.target.style.backgroundColor = '#545b62'}
          onMouseOut={(e) => e.target.style.backgroundColor = '#6c757d'}
        >
          Clear Filter
        </button>
        <button
          type="button"
          onClick={() => setIncludePast(!includePast)}
          style={{
            padding: '0.6rem 1.2rem',
            backgroundColor: includePast ? '#28a745' : '#17a2b8',
            color: 'white',
            border: 'none',
            borderRadius: '4px',
            cursor: 'pointer',
            fontSize: '0.9rem',
            fontWeight: '500',
            marginTop: '1.4rem',
            transition: 'background-color 0.2s'
          }}
          onMouseOver={(e) => e.target.style.backgroundColor = includePast ? '#218838' : '#138496'}
          onMouseOut={(e) => e.target.style.backgroundColor = includePast ? '#28a745' : '#17a2b8'}
        >
          {includePast ? 'Excluding Past' : 'Include Past'}
        </button>
      </form>

      {loading && <p style={{ padding: '1rem', color: '#666', fontStyle: 'italic' }}>Loading...</p>}
      {error && <p style={{ padding: '1rem', color: '#dc3545', backgroundColor: '#f8d7da', border: '1px solid #f5c6cb', borderRadius: '4px' }}>{error}</p>}

      {/* Toggle Button for Unavailable Gatherings */}
      {gatherings.length > 0 && (
        <div style={{
          marginBottom: '1rem',
          display: 'flex',
          alignItems: 'center',
          gap: '0.5rem',
          padding: '0.75rem',
          backgroundColor: '#f9fafb',
          borderRadius: '6px',
          border: '1px solid #e5e7eb'
        }}>
          <button
            onClick={() => setShowUnavailable(!showUnavailable)}
            style={{
              padding: '0.5rem 1rem',
              backgroundColor: showUnavailable ? '#ef4444' : '#3b82f6',
              color: '#fff',
              border: 'none',
              borderRadius: '4px',
              cursor: 'pointer',
              fontWeight: '600',
              fontSize: '0.9rem',
              transition: 'background-color 0.2s ease'
            }}
          >
            {showUnavailable ? 'Hide Unavailable' : `Show Unavailable (${unavailableCount})`}
          </button>
          <span style={{ fontSize: '0.85rem', color: '#6b7280' }}>
            {showUnavailable
              ? `Showing all gatherings (${unavailableCount} unavailable)`
              : `Showing available gatherings only`
            }
          </span>
        </div>
      )}

      <div className="admin-total-count" style={{
        marginBottom: '1rem',
        padding: '0.75rem 1rem',
        backgroundColor: '#e7f3ff',
        border: '1px solid #b3d9ff',
        borderRadius: '6px',
        display: 'inline-block'
      }}>
        <span style={{ fontSize: '0.9rem', color: '#004085' }}>
          Total gatherings: <strong style={{ fontSize: '1.1rem', color: '#003366' }}>{sortedGatherings.length}</strong>
        </span>
        <span style={{ fontSize: '0.9rem', color: '#004085', marginLeft: '1.5rem' }}>
          Signup total: <strong style={{ fontSize: '1.1rem', color: '#003366' }}>{totalSignups}</strong>
        </span>
        <span style={{ fontSize: '0.9rem', color: '#004085', marginLeft: '1.5rem' }}>
          Overall gender ratio: <strong style={{ fontSize: '1.1rem', color: '#003366' }}>{overallGenderRatio}</strong>
        </span>
        <span style={{ fontSize: '0.9rem', color: '#004085', marginLeft: '1.5rem' }}>
          Female %: <strong style={{ fontSize: '1.1rem', color: '#003366' }}>{femalePercentage}%</strong>
        </span>
      </div>

      <div style={{ overflowX: 'auto', boxShadow: '0 2px 8px rgba(0,0,0,0.1)', borderRadius: '8px' }}>
        <table className="admin-gatherings-table" style={{ marginTop: '0', borderCollapse: 'collapse', width: '100%', minWidth: window.innerWidth <= 768 ? '100%' : '1200px', backgroundColor: 'white' }}>
          <thead>
            <tr style={{ borderBottom: '2px solid #dee2e6', backgroundColor: '#f8f9fa' }}>
              <th
                style={{ padding: '0.75rem', cursor: 'pointer', textAlign: 'left', fontWeight: '600', fontSize: '0.9rem', color: '#495057', userSelect: 'none' }}
                onClick={() => handleSort('gathering_datetime')}
              >
                Gathering Date{getSortIndicator('gathering_datetime')}
              </th>
              <th
                style={{ padding: '0.75rem', cursor: 'pointer', textAlign: 'left', fontWeight: '600', fontSize: '0.9rem', color: '#495057', userSelect: 'none' }}
                onClick={() => handleSort('city')}
              >
                City{getSortIndicator('city')}
              </th>
              <th
                style={{ padding: '0.75rem', cursor: 'pointer', textAlign: 'left', fontWeight: '600', fontSize: '0.9rem', color: '#495057', userSelect: 'none' }}
                onClick={() => handleSort('area')}
              >
                Area{getSortIndicator('area')}
              </th>
              <th
                style={{ padding: '0.75rem', cursor: 'pointer', textAlign: 'left', fontWeight: '600', fontSize: '0.9rem', color: '#495057', userSelect: 'none' }}
                onClick={() => handleSort('theme_name')}
              >
                Theme{getSortIndicator('theme_name')}
              </th>
              <th
                style={{ padding: '0.75rem', cursor: 'pointer', textAlign: 'left', fontWeight: '600', fontSize: '0.9rem', color: '#495057', userSelect: 'none' }}
                onClick={() => handleSort('signup_count')}
              >
                Signup Count{getSortIndicator('signup_count')}
              </th>
              <th style={{ padding: '0.75rem', textAlign: 'left', fontWeight: '600', fontSize: '0.9rem', color: '#495057' }}>
                Male:Female Ratio
              </th>
              <th style={{ padding: '0.75rem', textAlign: 'left', fontWeight: '600', fontSize: '0.9rem', color: '#495057' }}>
                Days Until/Since
              </th>
              <th style={{ padding: '0.75rem', textAlign: 'left', fontWeight: '600', fontSize: '0.9rem', color: '#495057' }}>
                Available
              </th>
              <th style={{ padding: '0.75rem', textAlign: 'left', fontWeight: '600', fontSize: '0.9rem', color: '#495057' }}>
                Customer Manager
              </th>
              <th style={{ padding: '0.75rem', textAlign: 'left', fontWeight: '600', fontSize: '0.9rem', color: '#495057' }}>
                Venue
              </th>
              <th style={{ padding: '0.75rem', textAlign: 'left', fontWeight: '600', fontSize: '0.9rem', color: '#495057' }}>
                Venue Booked
              </th>
              <th style={{ padding: '0.75rem', textAlign: 'left', fontWeight: '600', fontSize: '0.9rem', color: '#495057' }}>
                Venue Phoned
              </th>
              <th style={{ padding: '0.75rem', textAlign: 'left', fontWeight: '600', fontSize: '0.9rem', color: '#495057' }}>
                Actions
              </th>
            </tr>
          </thead>
          <tbody>
            {sortedGatherings.map((g, idx) => (
              <GatheringRow
                key={idx}
                g={g}
                idx={idx}
                isEditing={editingGatheringId === g.gathering_id}
                editValues={editValues}
                venues={venues}
                loading={loading}
                formatDateTime={formatDateTime}
                needsSignupAlert={needsSignupAlert}
                isRatioImbalanced={isRatioImbalanced}
                getMaleToFemaleRatio={getMaleToFemaleRatio}
                calculateDaysUntilSince={calculateDaysUntilSince}
                handleEditChange={handleEditChange}
                saveGatheringUpdates={saveGatheringUpdates}
                cancelEditing={cancelEditing}
                startEditing={startEditing}
                handleDetailsClick={handleDetailsClick}
              />
            ))}
          </tbody>
        </table>
      </div>

      {sortedGatherings.length === 0 && !loading && (
        <div style={{
          marginTop: '2rem',
          padding: '2rem',
          textAlign: 'center',
          backgroundColor: '#f8f9fa',
          borderRadius: '8px',
          border: '1px solid #dee2e6'
        }}>
          <p style={{ margin: 0, color: '#6c757d', fontSize: '1rem' }}>No gatherings found</p>
        </div>
      )}
    </div>
  );
}

export default AdminUserGatherings;
